import { useUser } from '@clerk/clerk-react'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

export interface UseProfileSettingsReturn {
  firstName: string
  lastName: string
  imageUrl: string | null
  isNameLoading: boolean
  isAvatarLoading: boolean
  handleNameChange: (firstName: string, lastName: string) => Promise<void>
  handleAvatarChange: (file: File | null) => Promise<void>
  handleAvatarRemove: () => Promise<void>
}

export const useProfileSettings = (): UseProfileSettingsReturn => {
  const { user } = useUser()

  const [isNameLoading, setIsNameLoading] = useState(false)
  const [isAvatarLoading, setIsAvatarLoading] = useState(false)

  const { handleNameChange } = useUserNameHandlers({
    user,
    setIsNameLoading,
  })

  const { handleAvatarChange, handleAvatarRemove } = useUserAvatarHandlers({
    user,
    setIsAvatarLoading,
  })

  return {
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    imageUrl: user?.imageUrl ?? null,
    isNameLoading,
    isAvatarLoading,
    handleNameChange,
    handleAvatarChange,
    handleAvatarRemove,
  }
}

interface UserNameHandlersParams {
  user: ReturnType<typeof useUser>['user']
  setIsNameLoading: (loading: boolean) => void
}

const useUserNameHandlers = ({
  user,
  setIsNameLoading,
}: UserNameHandlersParams): {
  handleNameChange: (firstName: string, lastName: string) => Promise<void>
} => {
  const handleNameChange = useCallback(
    async (firstName: string, lastName: string): Promise<void> => {
      if (!user) return
      setIsNameLoading(true)
      try {
        await user.update({ firstName, lastName })
        trackEvent('profile_updated', {})
        toast.success('Profile name updated successfully')
      } catch (error) {
        logger.error('Failed to update profile name:', error)
        toast.error('Failed to update profile name')
      } finally {
        setIsNameLoading(false)
      }
    },
    [user, setIsNameLoading]
  )

  return { handleNameChange }
}

interface UserAvatarHandlersParams {
  user: ReturnType<typeof useUser>['user']
  setIsAvatarLoading: (loading: boolean) => void
}

const useUserAvatarHandlers = ({
  user,
  setIsAvatarLoading,
}: UserAvatarHandlersParams): {
  handleAvatarChange: (file: File | null) => Promise<void>
  handleAvatarRemove: () => Promise<void>
} => {
  const handleAvatarChange = useCallback(
    async (file: File | null): Promise<void> => {
      if (!user || !file) return
      setIsAvatarLoading(true)
      try {
        await user.setProfileImage({ file })
        toast.success('Profile picture updated successfully')
      } catch (error) {
        logger.error('Failed to update profile picture:', error)
        toast.error('Failed to update profile picture')
      } finally {
        setIsAvatarLoading(false)
      }
    },
    [user, setIsAvatarLoading]
  )

  const handleAvatarRemove = useCallback(async (): Promise<void> => {
    if (!user) return
    setIsAvatarLoading(true)
    try {
      await user.setProfileImage({ file: null })
      toast.success('Profile picture removed successfully')
    } catch (error) {
      logger.error('Failed to remove profile picture:', error)
      toast.error('Failed to remove profile picture')
    } finally {
      setIsAvatarLoading(false)
    }
  }, [user, setIsAvatarLoading])

  return { handleAvatarChange, handleAvatarRemove }
}
